import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SearchSelect.module.scss';
import React from 'react';

export interface SearchSelectOption {
    id: string;
    name: string;
    /** Вторая строка под названием: ИНН, город — что поможет различить тёзок. */
    hint?: string;
}

export interface SearchSelectProps {
    options: SearchSelectOption[];
    value: string;
    onChange: (id: string) => void;
    placeholder?: string;
    emptyText?: string;
    disabled?: boolean;
    loading?: boolean;
    invalid?: boolean;
    id?: string;
}

export function SearchSelect({
    options,
    value,
    onChange,
    placeholder = 'Выберите значение',
    emptyText = 'Ничего не найдено',
    disabled = false,
    loading = false,
    invalid = false,
    id,
}: SearchSelectProps) {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState('');
    const [cursor, setCursor] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const selected = options.find((option) => option.id === value) ?? null;

    const matches = useMemo(() => {
        const query = term.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) =>
            `${option.name} ${option.hint ?? ''}`.toLowerCase().includes(query),
        );
    }, [options, term]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    /* Подсветка не должна уезжать за пределы видимой части списка. */
    useEffect(() => {
        if (!open) return;
        const item = listRef.current?.children[cursor] as HTMLElement | undefined;
        item?.scrollIntoView({ block: 'nearest' });
    }, [cursor, open]);

    const openList = () => {
        if (disabled) return;
        setOpen(true);
        setTerm('');
        setCursor(Math.max(0, matches.findIndex((option) => option.id === value)));
    };

    const pick = (optionId: string) => {
        onChange(optionId);
        setOpen(false);
        setTerm('');
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
            event.preventDefault();
            openList();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setCursor((prev) => Math.min(prev + 1, matches.length - 1));
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setCursor((prev) => Math.max(prev - 1, 0));
        }
        if (event.key === 'Enter' && matches[cursor]) {
            event.preventDefault();
            pick(matches[cursor].id);
        }
    };

    return (
        <div className={styles.root} ref={rootRef}>
            <button
                type="button"
                id={id}
                className={invalid ? styles.controlInvalid : styles.control}
                disabled={disabled || loading}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => (open ? setOpen(false) : openList())}
                onKeyDown={onKeyDown}
            >
                <span className={selected ? styles.value : styles.placeholder}>
                    {loading ? 'Загружаем…' : (selected?.name ?? placeholder)}
                </span>
                <i className={open ? styles.chevronOpen : styles.chevron} aria-hidden="true" />
            </button>

            {open && (
                <div className={styles.popup}>
                    <input
                        className={styles.search}
                        value={term}
                        autoFocus
                        placeholder="Начните вводить название или ИНН"
                        onChange={(event) => {
                            setTerm(event.target.value);
                            setCursor(0);
                        }}
                        onKeyDown={onKeyDown}
                    />

                    {matches.length === 0 ? (
                        <div className={styles.empty}>{emptyText}</div>
                    ) : (
                        <ul className={styles.list} role="listbox" ref={listRef}>
                            {matches.map((option, index) => (
                                <li
                                    key={option.id}
                                    role="option"
                                    aria-selected={option.id === value}
                                    className={
                                        index === cursor
                                            ? styles.optionActive
                                            : option.id === value
                                                ? styles.optionSelected
                                                : styles.option
                                    }
                                    onMouseEnter={() => setCursor(index)}
                                    onClick={() => pick(option.id)}
                                >
                                    <span className={styles.optionName}>{option.name}</span>
                                    {option.hint && <span className={styles.optionHint}>{option.hint}</span>}
                                </li>
                            ))}
                        </ul>
                    )}

                    {matches.length > 0 && (
                        <div className={styles.more}>Всего: {matches.length}</div>
                    )}
                </div>
            )}
        </div>
    );
}